import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  SafeAreaView,
  Dimensions,
  Platform,
} from 'react-native';
// react-native-maps does not work on web - conditionally import
let MapView, Marker, PROVIDER_GOOGLE, Polyline;
if (Platform.OS !== 'web') {
  const RNMaps = require('react-native-maps');
  MapView = RNMaps.default;
  Marker = RNMaps.Marker;
  PROVIDER_GOOGLE = RNMaps.PROVIDER_GOOGLE;
  Polyline = RNMaps.Polyline;
}
const isWeb = Platform.OS === 'web';
import * as Location from 'expo-location';
import { Ionicons } from '@expo/vector-icons';
import api from '../../config/api';

const { width, height } = Dimensions.get('window');

const RouteMapScreen = ({ route, navigation }) => {
  const { ride, destination, destinationType } = route.params;
  const [currentLocation, setCurrentLocation] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [estimatedArrival, setEstimatedArrival] = useState('5 min');
  const [rideStatus, setRideStatus] = useState('accepted');
  
  const mapRef = useRef(null);

  useEffect(() => {
    getCurrentLocation();
    generateDummyRoute();
  }, []);

  const getCurrentLocation = async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission denied', 'Location permission is required');
        return;
      }

      const location = await Location.getCurrentPositionAsync({});
      setCurrentLocation({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      });
    } catch (error) {
      console.error('Error getting location:', error);
      Alert.alert('Error', 'Could not get your location');
    }
  };

  const generateDummyRoute = () => {
    // Generate dummy route coordinates
    // In a real app, you'd use Google Directions API or similar
    const dummyRoute = [
      { latitude: 37.78825, longitude: -122.4324 },
      { latitude: 37.78925, longitude: -122.4334 },
      { latitude: 37.79025, longitude: -122.4344 },
      { latitude: 37.79125, longitude: -122.4354 },
      { latitude: 37.79225, longitude: -122.4364 },
    ];
    setRouteCoordinates(dummyRoute);
  };

  const handleStartNavigation = () => {
    setIsNavigating(true);
    Alert.alert(
      'Navigation Started',
      `Navigate to ${destinationType === 'pickup' ? 'pickup location' : 'destination'}`
    );
  };

  const handleArrived = async () => {
    if (destinationType === 'pickup') {
      Alert.alert(
        'Arrived at Pickup',
        'You have arrived at the pickup location. Contact the rider.',
        [
          { text: 'Call Rider', onPress: () => console.log('Call rider') },
          {
            text: 'Start Trip',
            onPress: () => {
              setRideStatus('in_progress');
              navigation.setParams({
                destination: ride.destination,
                destinationType: 'destination',
              });
              Alert.alert('Trip Started', 'Navigate to destination?');
            },
          },
        ]
      );
    } else {
      Alert.alert(
        'Trip Completed',
        'You have arrived at the destination. Complete the ride?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Complete Ride',
            onPress: async () => {
              try {
                // In a real app, you'd call your backend
                // await api.post('/rides/complete', { rideId: ride.id });
                
                Alert.alert(
                  'Ride Completed!',
                  `Fare: $${ride.estimatedFare}\nThank you for using Obzidn!`,
                  [
                    {
                      text: 'OK',
                      onPress: () => navigation.navigate('MainTabs'),
                    },
                  ]
                );
              } catch (error) {
                console.error('Error completing ride:', error);
                Alert.alert('Error', 'Failed to complete ride');
              }
            },
          },
        ]
      );
    }
  };

  const getDestinationCoordinates = () => {
    // In a real app, you'd geocode the address
    // For now, return dummy coordinates
    return {
      latitude: 37.79225,
      longitude: -122.4364,
    };
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Map */}
      <View style={styles.mapContainer}>
        {isWeb ? (
          <View style={styles.webMapPlaceholder}>
            <Ionicons name="navigate" size={48} color="#4CAF50" />
            <Text style={styles.webMapText}>
              {destinationType === 'pickup' ? 'Navigate to Pickup' : 'Navigate to Destination'}
            </Text>
            <Text style={styles.webMapSubtext}>{destination}</Text>
            <TouchableOpacity
              style={styles.webBackButton}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.webBackButtonText}>Go Back</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            <MapView
              ref={mapRef}
              style={styles.map}
              provider={PROVIDER_GOOGLE}
              region={{
                latitude: 37.78825,
                longitude: -122.4324,
                latitudeDelta: 0.05,
                longitudeDelta: 0.05,
              }}
              showsUserLocation={true}
              showsMyLocationButton={false}
            >
              {currentLocation && (
                <Marker
                  coordinate={currentLocation}
                  title="Your Location"
                  description="Driver"
                >
                  <View style={styles.driverMarker}>
                    <Ionicons name="car" size={20} color="#ffffff" />
                  </View>
                </Marker>
              )}
              <Marker
                coordinate={getDestinationCoordinates()}
                title={destinationType === 'pickup' ? 'Pickup Location' : 'Destination'}
                description={destination}
                pinColor={destinationType === 'pickup' ? '#4CAF50' : '#F44336'}
              />
              {routeCoordinates.length > 0 && (
                <Polyline
                  coordinates={routeCoordinates}
                  strokeColor="#000000"
                  strokeWidth={4}
                  lineDashPattern={[5, 5]}
                />
              )}
            </MapView>

            <View style={styles.navigationControls}>
              <TouchableOpacity
                style={styles.navigationButton}
                onPress={() => navigation.goBack()}
              >
                <Ionicons name="arrow-back" size={24} color="#000000" />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.navigationButton}
                onPress={getCurrentLocation}
              >
                <Ionicons name="locate" size={24} color="#000000" />
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>

      {/* Bottom Panel */}
      <View style={styles.bottomPanel}>
        {/* Trip Info */}
        <View style={styles.tripInfo}>
          <View style={styles.tripHeader}>
            <Text style={styles.tripTitle}>
              {destinationType === 'pickup' ? 'Navigate to Pickup' : 'Navigate to Destination'}
            </Text>
            <Text style={styles.estimatedTime}>ETA: {estimatedArrival}</Text>
          </View>

          <View style={styles.addressContainer}>
            <Ionicons 
              name={destinationType === 'pickup' ? 'location' : 'flag'} 
              size={20} 
              color={destinationType === 'pickup' ? '#4CAF50' : '#F44336'} 
            />
            <Text style={styles.addressText} numberOfLines={2}>
              {destination}
            </Text>
          </View>
        </View>

        {/* Rider Info */}
        <View style={styles.riderInfo}>
          <View style={styles.riderAvatar}>
            <Ionicons name="person" size={24} color="#666666" />
          </View>
          <View style={styles.riderDetails}>
            <Text style={styles.riderName}>{ride.rider.name}</Text>
            <Text style={styles.riderPhone}>+1 (555) 123-4567</Text>
          </View>
          <View style={styles.riderActions}>
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="call" size={20} color="#4CAF50" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="chatbubble" size={20} color="#2196F3" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          {!isNavigating ? (
            <TouchableOpacity
              style={styles.startButton}
              onPress={handleStartNavigation}
            >
              <Ionicons name="navigation" size={20} color="#ffffff" />
              <Text style={styles.startButtonText}>Start Navigation</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={styles.arrivedButton}
              onPress={handleArrived}
            >
              <Ionicons name="checkmark-circle" size={20} color="#ffffff" />
              <Text style={styles.arrivedButtonText}>
                {destinationType === 'pickup' ? 'Arrived at Pickup' : 'Arrived at Destination'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  map: {
    width: width,
    height: '100%',
  },
  webMapPlaceholder: {
    flex: 1,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  webMapText: {
    color: '#333',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 12,
  },
  webMapSubtext: {
    color: '#666',
    fontSize: 14,
    marginTop: 6,
    textAlign: 'center',
  },
  webBackButton: {
    marginTop: 20,
    backgroundColor: '#000',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  webBackButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  driverMarker: {
    backgroundColor: '#000000',
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navigationControls: {
    position: 'absolute',
    top: 20,
    left: 20,
    flexDirection: 'column',
  },
  navigationButton: {
    backgroundColor: '#ffffff',
    borderRadius: 25,
    width: 50,
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  bottomPanel: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingVertical: 20,
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  tripInfo: {
    marginBottom: 20,
  },
  tripHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  tripTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333333',
  },
  estimatedTime: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4CAF50',
  },
  addressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f8f8',
    borderRadius: 8,
    padding: 12,
  },
  addressText: {
    flex: 1,
    fontSize: 14,
    color: '#333333',
    marginLeft: 10,
  },
  riderInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f8f8',
    borderRadius: 12,
    padding: 15,
    marginBottom: 20,
  },
  riderAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#E0E0E0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  riderDetails: {
    flex: 1,
  },
  riderName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 2,
  },
  riderPhone: {
    fontSize: 14,
    color: '#666666',
  },
  riderActions: {
    flexDirection: 'row',
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 10,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  startButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    paddingVertical: 15,
    paddingHorizontal: 30,
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  startButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  arrivedButton: {
    backgroundColor: '#000000',
    borderRadius: 12,
    paddingVertical: 15,
    paddingHorizontal: 30,
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  arrivedButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

export default RouteMapScreen;
